import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import type { DataGridColumn } from '@components/atoms/datatable/data-grid-v2';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { useVoiceProfileCapabilities } from '@utils/api/organizations/speakers/voice-profiles';
import { VoiceSampleCountCell } from '@components/speakers/voice-sample-count-cell.tsx';

export type SpeakerRow = {
  id: number;
  name: string;
  role: string;
  party: string;
  email: string;
  notes: string;
};

const TEXT_COL = (
  id: keyof Omit<SpeakerRow, 'id'>,
  header: string,
  minWidth: number,
  filterable = true
): DataGridColumn<SpeakerRow> => ({
  id,
  header,
  field: id,
  minWidth,
  enableSorting: filterable,
  enableFiltering: filterable,
  filterVariant: filterable ? 'text' : undefined,
  filterKey: filterable ? id : undefined,
  serverOperator: filterable ? 'ilike' : undefined,
  cell: ({ getValue }) => String(getValue() ?? '')
});

export const useSpeakerColumns = (): DataGridColumn<SpeakerRow>[] => {
  const { t } = useTranslation();
  const { isAdmin, isSysOp } = useRoles();

  // Sample metadata is admin-only on the backend, so only ask for readiness
  // when the answer could add a column.
  const canManageVoice = isAdmin || isSysOp;
  const { data: capabilities } = useVoiceProfileCapabilities(canManageVoice);

  // The column is added only when the organization has opted in: every other
  // directory keeps exactly the grid it has today, and no per-row sample
  // request is ever sent.
  const showVoiceColumn = canManageVoice && !!capabilities?.enabled;

  return useMemo(
    () => [
      {
        ...TEXT_COL('name', t('name'), 200),
        cell: ({ getValue }) => (
          <Text as="span" size="sm" weight="medium">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      TEXT_COL('role', t('role'), 150),
      TEXT_COL('party', t('party'), 150),
      TEXT_COL('email', t('email'), 180),
      TEXT_COL('notes', t('notes'), 200, false),
      ...(showVoiceColumn
        ? [
            {
              id: 'voiceSamples',
              header: t('voice-samples'),
              minWidth: 120,
              // No server-side column to sort or filter on.
              enableSorting: false,
              enableFiltering: false,
              cell: ({ row }: { row: { original: SpeakerRow } }) => (
                <VoiceSampleCountCell speakerId={row.original.id} />
              )
            } as DataGridColumn<SpeakerRow>
          ]
        : [])
    ],
    [t, showVoiceColumn]
  );
};
